import React, { useCallback, useEffect, useState } from 'react';
import { toast as message } from 'sonner';
import EnhancedDialog from '../EnhancedDialog';
import {
  requestDealPrepHistory,
  type DealPrepHistoryResult,
  type DealPrepHistoryScope,
} from './dealPrepHistory';
import '../SendOffer/SendOfferDialog.css';
import './DealPrepHistoryDialog.css';

const DEFAULT_SCOPE: DealPrepHistoryScope = { allCycles: false, dealCycleId: null };
const ALL_CYCLES = 'all';

/**
 * REG-721: "Send history to DEAL PREP" from the merchant panel. The dialog is
 * a thin shell over the deal-prep-history-email edge function: it asks for a
 * preview (rendered HTML + envelope + the merchant's cycles), lets the admin
 * pick one deal cycle (default = most recent) or every cycle, and sends.
 * The envelope is decided server-side and shown read-only: From = the
 * merchant's main rep (their login; admin@ when they have none or are one
 * of the admin senders in Global Settings - Zack / David), To preps@, Cc
 * admin@ + jfox@ + bella@ (always) + the main
 * rep + the merchant's additional reps (merchant-wide rep_links;
 * commission-only and removed reps are never included).
 */
export function DealPrepHistoryDialog({
  merchantId,
  isOpen,
  onClose,
}: {
  merchantId: number;
  isOpen: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  const [scope, setScope] = useState<DealPrepHistoryScope>(DEFAULT_SCOPE);
  const [preview, setPreview] = useState<DealPrepHistoryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(async (nextScope: DealPrepHistoryScope) => {
    setLoading(true);
    setError(null);
    const response = await requestDealPrepHistory({ merchantId, scope: nextScope, preview: true });
    if (!response.ok) {
      setError(response.error);
      setPreview(null);
    } else {
      setPreview(response.result);
      // Pin the resolved default so the picker reflects the cycle actually shown.
      setScope(response.result.scope);
    }
    setLoading(false);
  }, [merchantId]);

  useEffect(() => {
    if (!isOpen) return;
    setScope(DEFAULT_SCOPE);
    setPreview(null);
    setSending(false);
    void loadPreview(DEFAULT_SCOPE);
  }, [isOpen, loadPreview]);

  if (!isOpen) return null;

  const pickerValue = scope.allCycles ? ALL_CYCLES : scope.dealCycleId != null ? String(scope.dealCycleId) : '';
  const cycles = preview?.cycles ?? [];
  const canSend = Boolean(preview && preview.submissionCount > 0) && !loading && !sending;
  const busy = loading || sending;

  const handleScopeChange = (value: string) => {
    const next: DealPrepHistoryScope = value === ALL_CYCLES
      ? { allCycles: true, dealCycleId: null }
      : { allCycles: false, dealCycleId: Number(value) || null };
    setScope(next);
    void loadPreview(next);
  };

  const handleSend = async () => {
    if (!canSend || !preview) return;
    setSending(true);
    try {
      const response = await requestDealPrepHistory({ merchantId, scope, preview: false });
      if (!response.ok) throw new Error(response.error);
      const { result } = response;
      message.success(
        `Sent ${result.submissionCount} submission${result.submissionCount === 1 ? '' : 's'} to DEAL PREP ` +
        `(${result.to.join(', ')}; cc ${result.cc.join(', ')})`,
      );
      onClose();
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Could not send the history');
    } finally {
      setSending(false);
    }
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      title={`Send history to DEAL PREP${preview ? ` - ${preview.businessName}` : ''}`}
      className="send-offer-dialog deal-prep-dialog"
      canOutsideClickClose={false}
      canEscapeKeyClose={!busy}
    >
      <div className="sky-dialog-body">
        {error && (
          <div className="sky-callout sky-callout-warning" style={{ marginBottom: 12 }}>{error}</div>
        )}
        <div className="send-offer-envelope">
          <div className="deal-prep-scope-row">
            <label className="send-offer-envelope-label" htmlFor="deal-prep-cycle">Deal cycle</label>
            <select
              id="deal-prep-cycle"
              className="deal-prep-scope-select"
              data-testid="deal-prep-cycle-select"
              value={pickerValue}
              disabled={busy || cycles.length === 0}
              onChange={(e) => handleScopeChange(e.target.value)}
            >
              {cycles.length === 0 && <option value="">All submissions</option>}
              {cycles.map((cycle) => (
                <option key={cycle.id} value={String(cycle.id)}>
                  {cycle.label} ({cycle.submissionCount})
                </option>
              ))}
              {cycles.length > 0 && (
                <option value={ALL_CYCLES}>
                  All cycles ({cycles.reduce((n, c) => n + c.submissionCount, 0) + (preview?.unassignedCount ?? 0)})
                </option>
              )}
            </select>
          </div>
          <div><span className="send-offer-envelope-label">From</span> <span data-testid="deal-prep-from">{preview?.from ?? '…'}</span></div>
          <div><span className="send-offer-envelope-label">To</span> <span data-testid="deal-prep-to">{(preview?.to ?? ['preps@skylinecaps.com']).join(', ')}</span></div>
          <div><span className="send-offer-envelope-label">Cc</span> <span data-testid="deal-prep-cc">{preview ? preview.cc.join(', ') : '…'}</span></div>
          <div><span className="send-offer-envelope-label">Subject</span> <span data-testid="deal-prep-subject">{preview?.subject ?? '…'}</span></div>
        </div>
        <p className="send-offer-hint deal-prep-hint">
          Same-subject mail lands in the merchant&#39;s existing DEAL PREP conversation. Sent from the
          merchant&#39;s main rep; Cc admin@, jfox@, bella@, the main rep and the merchant&#39;s additional reps.
          {preview?.fromAdminSender
            ? ' The main rep is an admin sender (Global Settings), so admin@ sends this one.'
            : preview?.fromFallback
              ? ' The main rep has no CRM login, so admin@ sends this one.'
              : ''}
        </p>
        {loading && !preview ? (
          <div className="deal-prep-loading"><span className="sky-spinner" /> Building history&hellip;</div>
        ) : (
          <iframe
            className="send-offer-preview deal-prep-preview"
            title="DEAL PREP history preview"
            sandbox=""
            srcDoc={preview?.html ?? ''}
          />
        )}
      </div>
      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button
          className="sky-btn sky-btn-primary"
          data-testid="deal-prep-send"
          onClick={handleSend}
          disabled={!canSend}
          aria-busy={sending}
        >
          {sending ? <span className="sky-spinner" /> : null}
          {sending ? 'Sending…' : preview ? `Send ${preview.submissionCount} to DEAL PREP` : 'Send to DEAL PREP'}
        </button>
      </div>
    </EnhancedDialog>
  );
}

export default DealPrepHistoryDialog;
